/**
 * Errors raised by scripts that a host app injects into our page, which end up
 * in Sentry only because we install global error handlers. They contain no
 * frame from our bundle and there is nothing in the app to fix.
 */

/** A stack frame as seen by Sentry's beforeSend; both fields may be absent. */
export type NoiseFrame = {
  filename?: string | null;
  function?: string | null;
};

/**
 * The Facebook in-app browser (FB_IAB on Android) injects a
 * `navigation_performance_logger_android` bundle that reports timings to the
 * native app over a JavaScript bridge. During teardown - `beforeunload`, or a
 * soft navigation inside our SPA - the Java side of that bridge is already
 * gone, so the call throws. Facebook's script failing in Facebook's WebView.
 */
const FB_IN_APP_BROWSER_BRIDGE_PATTERNS = [
  // Observed variants: "Java object is gone" (the WebView is already torn
  // down) and "Java exception was raised during method invocation".
  /Error invoking postMessage:\s*Java\b/i,
];

/** Injected-script URL scheme used by the Facebook in-app browser bridge. */
export const FB_IN_APP_BROWSER_BRIDGE_URL_PATTERN = /^iabjs:\/\//i;

/**
 * The Facebook in-app browser on iOS injects an inline script directly into
 * the served HTML document, so its stack frames carry OUR page URL as the
 * filename - only the function names identify it. The script bridges
 * login-form and performance telemetry to the native app (postMessage /
 * window.webkit.messageHandlers) and throws InvalidAccessError when the
 * bridge rejects the call. None of these function names exist in our
 * codebase; the Android sibling (`sendDataToNative`, `sendINPMessage`)
 * normally also matches the `iabjs://` filename pattern above.
 */
const INJECTED_BRIDGE_FUNCTION_NAMES = new Set([
  "dispatchToBridge",
  "sendPostMessage",
  "logLoginFormFocused",
  "sendDataToNative",
  "sendINPMessage",
]);

/** Frames from our own Vite bundle; never treated as injected even on a name collision. */
const APP_BUNDLE_FILENAME_PATTERN = /\/assets\//i;

/**
 * The same injected FB iOS script probing the native bridge before it exists:
 * "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers...')".
 * Our code never references window.webkit.messageHandlers, so any message
 * mentioning it is the in-app browser's own bridge code.
 */
export const WEBKIT_MESSAGE_HANDLERS_PATTERN = /window\.webkit\.messageHandlers/i;

/**
 * The Google app / Chrome iOS in-app browser injects a script whose infinite
 * recursion surfaces as "RangeError: Maximum call stack size exceeded" with
 * every frame's filename literally "undefined". Drop ONLY when the message
 * matches AND there is at least one frame AND every frame is non-attributable,
 * so any frame naming a real file - even our minified bundle - keeps the event.
 */
const MAX_CALL_STACK_PATTERN = /Maximum call stack size exceeded/i;
const NON_ATTRIBUTABLE_FRAME_FILENAMES = new Set([
  "",
  "undefined",
  "<anonymous>",
  "[native code]",
]);

/**
 * Our served HTML (`apps/web/index.html`, 16 lines after the Vite build) carries
 * no inline script: every line of ours lives under `/assets/`. A frame whose
 * filename is the page document itself therefore belongs to a script some host
 * injected inline — Chrome on iOS (observed 2026-10-12..19: "Error: La" and a
 * max-call-stack recursion at lines 190-500 of `/chats/...`), the FB iOS
 * in-app browser, page translators. The frame is matched structurally
 * (same-origin http(s) URL or bare path, outside `/assets/`, last path segment
 * without a file extension) so we never depend on a browser's exact wording.
 */
export function isServedDocumentFrame(filename: string | null | undefined): boolean {
  const trimmed = (filename ?? "").trim();
  if (trimmed === "") {
    return false;
  }
  let pathname: string;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      pathname = new URL(trimmed).pathname;
    } catch {
      return false;
    }
  } else if (trimmed.startsWith("/")) {
    pathname = trimmed.split(/[?#]/, 1)[0];
  } else {
    // iabjs://, chrome-extension://, app:///, "undefined", "[native code]"...
    return false;
  }
  if (APP_BUNDLE_FILENAME_PATTERN.test(pathname)) {
    return false;
  }
  const lastSegment = pathname.slice(pathname.lastIndexOf("/") + 1);
  return !lastSegment.includes(".");
}

export function isFacebookInAppBrowserBridgeMessage(message: string): boolean {
  return FB_IN_APP_BROWSER_BRIDGE_PATTERNS.some((pattern) => pattern.test(message));
}

export function isWebKitMessageHandlersProbeMessage(message: string): boolean {
  return WEBKIT_MESSAGE_HANDLERS_PATTERN.test(message);
}

export function hasFacebookInAppBrowserBridgeFrame(
  filenames: Array<string | null | undefined>
): boolean {
  return filenames.some(
    (filename) => !!filename && FB_IN_APP_BROWSER_BRIDGE_URL_PATTERN.test(filename)
  );
}

export function hasInjectedBridgeFunctionFrame(frames: NoiseFrame[]): boolean {
  return frames.some((frame) => {
    const functionName = frame.function?.trim() ?? "";
    if (!INJECTED_BRIDGE_FUNCTION_NAMES.has(functionName)) {
      return false;
    }
    return !APP_BUNDLE_FILENAME_PATTERN.test(frame.filename ?? "");
  });
}

export function hasOnlyNonAttributableFrames(
  filenames: Array<string | null | undefined>
): boolean {
  if (filenames.length === 0) {
    return false;
  }
  return filenames.every((filename) =>
    NON_ATTRIBUTABLE_FRAME_FILENAMES.has((filename ?? "").trim())
  );
}

/**
 * True when an event is Facebook in-app browser bridge noise: the message
 * matches the bridge's own error text, probes window.webkit.messageHandlers,
 * or the stack trace is rooted in the injected script (`iabjs://` filenames on
 * Android, telltale bridge function names inside the served page on iOS).
 */
export function isFacebookInAppBrowserBridgeNoise(input: {
  messages: string[];
  frames: NoiseFrame[];
}): boolean {
  return (
    input.messages.some((message) => isFacebookInAppBrowserBridgeMessage(message)) ||
    input.messages.some((message) => isWebKitMessageHandlersProbeMessage(message)) ||
    hasFacebookInAppBrowserBridgeFrame(input.frames.map((frame) => frame.filename)) ||
    hasInjectedBridgeFunctionFrame(input.frames)
  );
}

export function isInjectedScriptMaxCallStackNoise(input: {
  messages: string[];
  frames: NoiseFrame[];
}): boolean {
  return (
    input.messages.some((message) => MAX_CALL_STACK_PATTERN.test(message)) &&
    hasOnlyNonAttributableFrames(input.frames.map((frame) => frame.filename))
  );
}

/**
 * True when the stack is rooted in the served document: at least one frame is
 * the page itself and every frame is either that or non-attributable. One frame
 * from any real file - our bundle, a third-party SDK, an extension - keeps the
 * event, whatever the message says.
 */
export function isInjectedInlineScriptNoise(input: { frames: NoiseFrame[] }): boolean {
  const filenames = input.frames.map((frame) => frame.filename);
  if (!filenames.some(isServedDocumentFrame)) {
    return false;
  }
  return filenames.every(
    (filename) =>
      isServedDocumentFrame(filename) ||
      NON_ATTRIBUTABLE_FRAME_FILENAMES.has((filename ?? "").trim())
  );
}

/** The single drop decision applied by Sentry's beforeSend in main.tsx. */
export function isThirdPartyInjectedNoise(input: {
  messages: string[];
  frames: NoiseFrame[];
}): boolean {
  return (
    isFacebookInAppBrowserBridgeNoise(input) ||
    isInjectedScriptMaxCallStackNoise(input) ||
    isInjectedInlineScriptNoise(input)
  );
}
